"use client";

import {
  CalendarCheck,
  CalendarX,
  Check,
  type LucideIcon,
  Trophy,
  UserMinus,
  UserPlus,
  Workflow,
} from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { SectionHeading } from "../../home/section";
import { Stagger, StaggerItem } from "../../home/motion";

/* ------------------------------------------------------------------ */
/* Shared mock chrome                                                 */
/* ------------------------------------------------------------------ */

function MiniCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border border-border bg-background shadow-sm", className)}>
      {children}
    </div>
  );
}

function CardHeader({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 border-b border-border px-3 py-2.5 text-[13px] font-semibold text-foreground">
      <Icon className="size-3.5 text-muted-foreground" />
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 1 · Built for fitness — triggers on real fitness moments           */
/* ------------------------------------------------------------------ */

function TriggerRow({
  icon: Icon,
  label,
  active,
}: {
  icon: LucideIcon;
  label: string;
  active?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 rounded-lg border px-3 py-2",
        active
          ? "border-primary/40 bg-primary/5 dark:border-brand-accent/40 dark:bg-brand-accent/5"
          : "border-border bg-background",
      )}
    >
      <Icon
        className={cn(
          "size-4 shrink-0",
          active ? "text-primary dark:text-brand-accent" : "text-muted-foreground",
        )}
      />
      <span className="text-[12px] font-semibold text-foreground">{label}</span>
      {active ? (
        <Check className="ml-auto size-4 shrink-0 text-primary dark:text-brand-accent" />
      ) : null}
    </div>
  );
}

function TriggerMock() {
  return (
    <MiniCard className="w-full max-w-[244px] overflow-hidden">
      <CardHeader icon={CalendarX}>Trigger</CardHeader>
      <div className="space-y-2 p-3">
        <TriggerRow icon={CalendarX} label="No-showed a class" active />
        <TriggerRow icon={UserMinus} label="Membership lapsed" />
        <TriggerRow icon={Trophy} label="Hit 100 classes" />
      </div>
    </MiniCard>
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Playbook built in — proven journeys, live on day one           */
/* ------------------------------------------------------------------ */

function FlowRow({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2">
      <span className="min-w-0 truncate text-[12px] font-medium text-foreground">{label}</span>
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success">
        <span className="size-1.5 rounded-full bg-success" />
        Active
      </span>
    </div>
  );
}

function PlaybookMock() {
  return (
    <MiniCard className="w-full max-w-[244px] overflow-hidden">
      <CardHeader icon={Workflow}>Your flows</CardHeader>
      <div className="space-y-1.5 p-3">
        <FlowRow label="New lead nurture" />
        <FlowRow label="No-show recovery" />
        <FlowRow label="Win-back" />
        <FlowRow label="Milestone rewards" />
      </div>
    </MiniCard>
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Enroll & exit automatically — right message, right moment      */
/* ------------------------------------------------------------------ */

function RuleRow({
  label,
  icon: Icon,
  value,
  tone,
}: {
  label: string;
  icon: LucideIcon;
  value: string;
  tone: "default" | "success";
}) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg border px-3 py-2",
          tone === "success" ? "border-success/30 bg-success/5" : "border-border bg-muted/40",
        )}
      >
        <Icon
          className={cn(
            "size-4 shrink-0",
            tone === "success" ? "text-success" : "text-muted-foreground",
          )}
        />
        <span className="min-w-0 truncate text-[12px] font-semibold text-foreground">{value}</span>
      </div>
    </div>
  );
}

function EnrollExitMock() {
  return (
    <MiniCard className="w-full max-w-[244px] overflow-hidden">
      <CardHeader icon={Workflow}>Win-back</CardHeader>
      <div className="space-y-3 p-3">
        <RuleRow
          label="Enrolls when"
          icon={UserPlus}
          value="Joins “Lapsed members”"
          tone="default"
        />
        <RuleRow label="Exits when" icon={CalendarCheck} value="Books a class" tone="success" />
      </div>
    </MiniCard>
  );
}

/* ------------------------------------------------------------------ */
/* Section                                                            */
/* ------------------------------------------------------------------ */

type Highlight = { visual: ReactNode; title: string; body: string };

const HIGHLIGHTS: Highlight[] = [
  {
    visual: <TriggerMock />,
    title: "Triggers that speak fitness",
    body: "Start journeys on the moments that matter: a first class booked, a no-show, a lapsed membership, a 100-class milestone. The signals generic tools can't see.",
  },
  {
    visual: <PlaybookMock />,
    title: "The playbook, ready on day one",
    body: "We set up the proven fitness journeys around your booking data during onboarding, so you launch already running best practices. No blank canvas to fill.",
  },
  {
    visual: <EnrollExitMock />,
    title: "Right message, right moment",
    body: "Flows enroll people the moment they match and exit them when they don't, so nobody hears “we miss you” the day after they came back.",
  },
];

export function FlowsHighlights() {
  return (
    <section className="bg-background pt-20 lg:pt-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <SectionHeading
          align="left"
          size="sm"
          title={
            <>
              Everyone feels remembered.
              <span className="text-muted-foreground">
                {" "}
                Helios handles the follow-up that grows a studio, welcoming newcomers, catching
                no-shows, and winning back the ones drifting away, each at just the right moment.
              </span>
            </>
          }
        />
      </div>

      {/* Framed band: hatched side gutters, solid card body (Attio-style). */}
      <div className="mt-12 border-t border-border bg-grid lg:mt-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <Stagger className="grid grid-cols-1 divide-y divide-border border-x border-border bg-background lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            {HIGHLIGHTS.map((h) => (
              <StaggerItem key={h.title} className="flex flex-col px-6 py-8 sm:px-8 sm:py-10">
                <div className="flex h-[248px] items-center justify-center">{h.visual}</div>
                <div className="mt-7">
                  <h3 className="text-base font-bold tracking-tight text-foreground">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.body}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}
